import { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Slider from '@material-ui/core/Slider';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import styles from './MapExport.module.css';
import {
    exportMapImage,
    downloadBlob,
    getOutputSize,
    ASPECT_RATIOS,
    SCALE_OPTIONS,
    FORMATS,
    TITLE_FONT_SCALES,
    LEGEND_SCALES,
    ORIENTATIONS,
    OVERLAY_THEMES,
    LEGEND_POSITIONS,
} from './exportMapImage';
import { withExportStyles } from './exportStyles';
import { allLayersGroups } from '../../../constants/layers';
import { layersRenderOrder } from '../../../constants/variables';

// Дропдауны в стилистике выбора города в левой панели: тёмное поле со светлой
// обводкой, белый выпадающий список. Общие пропсы, чтобы не повторять в каждом.
const SELECT_PROPS = {
    variant: 'outlined',
    margin: 'dense',
};
// Выпадающий список — белый и под полем (getContentAnchorEl: null снимает
// дефолтное для MUI v4 перекрытие поля списком).
const SELECT_MENU_PROPS = {
    classes: { paper: styles.menuPaper },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
};
const ASPECTS = Object.keys(ASPECT_RATIOS);
const FORMAT_KEYS = Object.keys(FORMATS);
const TITLE_FONT_KEYS = Object.keys(TITLE_FONT_SCALES);
const LEGEND_SCALE_KEYS = Object.keys(LEGEND_SCALES);
const ORIENTATION_KEYS = Object.keys(ORIENTATIONS);
const THEME_KEYS = Object.keys(OVERLAY_THEMES);
const LEGEND_POSITION_KEYS = Object.keys(LEGEND_POSITIONS);
// Целевая ширина превью (px). Масштаб подбирается под неё от реального размера кадра.
const PREVIEW_WIDTH = 360;
// Запоминаем последние настройки экспорта между сессиями (localStorage).
// Заголовок не храним — он зависит от текущей карты (авто-подстановка).
const STORAGE_KEY = 'martin.mapExport.settings';
const DEFAULT_SETTINGS = {
    aspect: '4:3',
    orientation: 'landscape',
    theme: 'dark',
    format: 'jpeg',
    quality: 0.92,
    titleFontScale: 1.4,
    scale: 1,
    includeLegend: true,
    legendPosition: 'top-right',
    legendScale: 1,
};
const loadSettings = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);

        return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : { ...DEFAULT_SETTINGS };
    } catch (e) {
        return { ...DEFAULT_SETTINGS };
    }
};
const saveSettings = settings => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
        // localStorage недоступен (приватный режим и т.п.) — молча игнорируем.
    }
};
// id слоя → объект слоя (для авто-заголовка и его перекрытия exportTitle).
const LAYERS_BY_ID = allLayersGroups.reduce((acc, group) => {
    (group.layers || []).forEach(layer => {
        if (layer && layer.id) {
            acc[layer.id] = layer;
        }
    });

    return acc;
}, {});
// Имя слоя для авто-заголовка экспорта. Приоритет — свойство exportTitle слоя,
// перекрывающее label ТОЛЬКО в заголовке растра (на карту/сайдбар не влияет):
//   - функция (ctx) => string — динамический заголовок от контролов
//     (ctx = { city, locale, filters, controls }); filters — слайс mapFilters,
//     controls — контролы вне mapFilters (map-слайс: дата ребаланса, час/дата
//     стартов); пустой результат → откат на label;
//   - defineMessage/строка — фиксированное перекрытие label.
// Иначе берём обычный label слоя.
// Фоновые/референсные слои: попадают в авто-заголовок экспорта лишь когда
// на карте больше ничего нет (см. autoTitle).
const BACKGROUND_LAYER_IDS = new Set([
    'Границы города',
    'Административное деление',
    'Границы тайлов',
    'Адреса гексагонов H3',
]);
const layerName = (id, ctx) => {
    const layer = LAYERS_BY_ID[id];
    if (!layer) {
        return id;
    }

    const fallback = layer.label ? i18n._(layer.label) : id;
    const override = layer.exportTitle;
    if (typeof override === 'function') {
        return override(ctx) || fallback;
    }
    if (override) {
        return i18n._(override);
    }

    return fallback;
};
const MapExportComponent = ({ getMap, selectedRegion, displayedLayers, mapFilters, selectedRebalanceDate, selectedRebalanceDayMode, selectedRebalancePeriod, selectedHour, selectedDate }) => {
    // Последние настройки пользователя (или дефолты) — читаем один раз при монтировании.
    const [ stored ] = useState(loadSettings);
    const [ open, setOpen ] = useState(false);
    // null — использовать авто-заголовок; '' — пользователь его убрал.
    const [ title, setTitle ] = useState(null);
    const [ aspect, setAspect ] = useState(stored.aspect);
    const [ orientation, setOrientation ] = useState(stored.orientation);
    const [ theme, setTheme ] = useState(stored.theme);
    const [ format, setFormat ] = useState(stored.format);
    const [ quality, setQuality ] = useState(stored.quality);
    const [ titleFontScale, setTitleFontScale ] = useState(stored.titleFontScale);
    const [ scale, setScale ] = useState(stored.scale);
    const [ includeLegend, setIncludeLegend ] = useState(stored.includeLegend);
    const [ legendPosition, setLegendPosition ] = useState(stored.legendPosition);
    const [ legendScale, setLegendScale ] = useState(stored.legendScale);
    const [ busy, setBusy ] = useState(false);
    const [ previewUrl, setPreviewUrl ] = useState(null);

    // Сохраняем настройки между сессиями при каждом изменении. Заголовок НЕ
    // храним — он зависит от текущей карты (авто-подстановка).
    useEffect(() => {
        saveSettings({ aspect, orientation, theme, format, quality, titleFontScale, scale, includeLegend, legendPosition, legendScale });
    }, [ aspect, orientation, theme, format, quality, titleFontScale, scale, includeLegend, legendPosition, legendScale ]);

    // При закрытии диалога сбрасываем заголовок в авто — чтобы следующее открытие
    // (в т.ч. в новой сессии) стартовало со свежего авто-заголовка под карту.
    useEffect(() => {
        if (!open) {
            setTitle(null);
        }
    }, [ open ]);
    // Авто-заголовок: «Город. Верхний слой». Верхний = с максимальным индексом
    // в порядке отрисовки (рисуется поверх остальных).
    const autoTitle = useMemo(() => {
        const city = selectedRegion && selectedRegion.name ? i18n._(selectedRegion.name) : '';
        const pickTop = layers => layers.reduce((top, l) => (
            layersRenderOrder.indexOf(l.id) >= layersRenderOrder.indexOf(top.id) ? l : top
        ), { id: null });
        // Фоновые/референсные слои (границы, сетки, адреса гексагонов) идут в
        // заголовок только когда других слоёв на карте нет — иначе их перебивает
        // любой содержательный слой.
        const shown = displayedLayers || [];
        const foreground = shown.filter(l => !BACKGROUND_LAYER_IDS.has(l.id));
        const topLayer = pickTop(foreground.length ? foreground : shown);
        const controls = { selectedRebalanceDate, selectedRebalanceDayMode, selectedRebalancePeriod, selectedHour, selectedDate };
        const ctx = { city, locale: i18n.locale, filters: mapFilters || {}, controls };
        const name = topLayer.id ? layerName(topLayer.id, ctx) : '';

        if (name) {
            return city ? `${city}. ${name}` : name;
        }

        return city;
    }, [ selectedRegion, displayedLayers, mapFilters, selectedRebalanceDate, selectedRebalanceDayMode, selectedRebalancePeriod, selectedHour, selectedDate ]);
    const titleValue = title === null ? autoTitle : title;
    // Реальные размеры растра (px) для каждого множителя при текущем формате.
    // Пересчёт при открытии диалога и смене формата.
    const resolutions = useMemo(() => {
        const map = getMap && getMap();
        return SCALE_OPTIONS.map(s => {
            if (!map) {
                return { scale: s, label: `${s}×` };
            }
            const { width, height } = getOutputSize({ map, scale: s, aspect, orientation });

            return { scale: s, label: `${width} × ${height} px` };
        });
    // `open` в зависимостях намеренно — пересчёт при открытии диалога (карта могла
    // измениться, пока диалог был закрыт); в теле не используется, отсюда варнинг.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ open, aspect, orientation, getMap ]);
    // Живое превью: тот же пайплайн экспорта в маленьком масштабе. Перегенерируем
    // при смене того, что влияет на пропорции/состав (формат листа, заголовок,
    // легенда, png/jpeg) — но не на качество/разрешение. Дебаунс гасит ввод в поле.
    useEffect(() => {
        if (!open) {
            setPreviewUrl(prev => {
                if (prev) {
                    URL.revokeObjectURL(prev);
                }

                return null;
            });

            return () => {};
        }

        let cancelled = false;
        let url = null;
        const timer = setTimeout(async () => {
            const map = getMap && getMap();
            if (!map) {
                return;
            }

            const base = getOutputSize({ map, scale: 1, aspect, orientation });
            const previewScale = base.width ? PREVIEW_WIDTH / base.width : 1;
            const styleCtx = { locale: i18n.locale, filters: mapFilters || {}, controls: { selectedRebalanceDate, selectedRebalanceDayMode, selectedRebalancePeriod, selectedHour, selectedDate } };
            const styleLayers = (displayedLayers || []).map(l => LAYERS_BY_ID[l.id]).filter(Boolean);
            const blob = await withExportStyles(map, styleLayers, styleCtx, () => exportMapImage({
                map,
                title: titleValue,
                includeLegend,
                scale: previewScale,
                aspect,
                format,
                titleFontScale,
                legendScale,
                orientation,
                theme,
                legendPosition,
            }));

            if (cancelled || !blob) {
                return;
            }
            url = URL.createObjectURL(blob);
            setPreviewUrl(prev => {
                if (prev) {
                    URL.revokeObjectURL(prev);
                }

                return url;
            });
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [ open, aspect, orientation, theme, format, includeLegend, legendPosition, legendScale, titleValue, titleFontScale, getMap, displayedLayers, mapFilters, selectedRebalanceDate, selectedRebalanceDayMode, selectedRebalancePeriod, selectedHour, selectedDate ]);

    const handleExport = async () => {
        const map = getMap && getMap();
        if (!map) {
            return;
        }

        setBusy(true);
        try {
            const styleCtx = { locale: i18n.locale, filters: mapFilters || {}, controls: { selectedRebalanceDate, selectedRebalanceDayMode, selectedRebalancePeriod, selectedHour, selectedDate } };
            const styleLayers = (displayedLayers || []).map(l => LAYERS_BY_ID[l.id]).filter(Boolean);
            const blob = await withExportStyles(map, styleLayers, styleCtx, () => exportMapImage({
                map,
                title: titleValue,
                includeLegend,
                scale,
                aspect,
                format,
                quality,
                titleFontScale,
                legendScale,
                orientation,
                theme,
                legendPosition,
            }));
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            downloadBlob(blob, `martin-export-${stamp}.${FORMATS[format].ext}`);
            setOpen(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button
                className={styles.exportButton}
                onClick={() => setOpen(true)}
                variant='contained'
                size='small'
                startIcon={<GetAppIcon />}
            >
                {t`Экспорт`}
            </Button>

            <Dialog
                open={open}
                onClose={() => !busy && setOpen(false)}
                maxWidth='md'
                PaperProps={{ className: styles.paper }}
            >
                <div className={styles.head}>{t`Экспорт карты в растр`}</div>
                <div className={styles.content}>
                    <span className={styles.label}>{t`Заголовок`}</span>
                    <div className={styles.titleRow}>
                        <TextField
                            className={classnames(styles.input, styles.titleField)}
                            placeholder={t`Необязательно`}
                            value={titleValue}
                            onChange={e => setTitle(e.target.value)}
                            fullWidth
                            margin='dense'
                        />
                        <Select
                            {...SELECT_PROPS}
                            className={classnames(styles.input, styles.fontSelect)}
                            MenuProps={SELECT_MENU_PROPS}
                            value={titleFontScale}
                            onChange={e => setTitleFontScale(e.target.value)}
                        >
                            {TITLE_FONT_KEYS.map(k => (
                                <MenuItem
                                    key={k}
                                    value={TITLE_FONT_SCALES[k]}
                                >
                                    {i18n._(k)}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    <div className={styles.body}>
                        <div className={styles.controls}>
                            <span className={styles.label}>{t`Ориентация`}</span>
                            <ButtonGroup
                                variant='contained'
                                size='small'
                                fullWidth
                            >
                                {ORIENTATION_KEYS.map(k => (
                                    <Button
                                        key={k}
                                        color={orientation === ORIENTATIONS[k] ? 'primary' : 'default'}
                                        onClick={() => setOrientation(ORIENTATIONS[k])}
                                    >
                                        {i18n._(k)}
                                    </Button>
                                ))}
                            </ButtonGroup>

                            <span className={styles.label}>{t`Тема условных`}</span>
                            <ButtonGroup
                                variant='contained'
                                size='small'
                                fullWidth
                            >
                                {THEME_KEYS.map(k => (
                                    <Button
                                        key={k}
                                        color={theme === OVERLAY_THEMES[k] ? 'primary' : 'default'}
                                        onClick={() => setTheme(OVERLAY_THEMES[k])}
                                    >
                                        {i18n._(k)}
                                    </Button>
                                ))}
                            </ButtonGroup>

                            <span className={styles.label}>{t`Пропорции`}</span>
                            <ButtonGroup
                                variant='contained'
                                size='small'
                                fullWidth
                            >
                                {ASPECTS.map(a => (
                                    <Button
                                        key={a}
                                        color={aspect === a ? 'primary' : 'default'}
                                        onClick={() => setAspect(a)}
                                    >
                                        {a}
                                    </Button>
                                ))}
                            </ButtonGroup>

                            <div className={styles.legendToggle}>
                                <label htmlFor='exportIncludeLegend'>{t`Условные обозначения`}</label>
                                <label className={styles.switch}>
                                    <input
                                        type='checkbox'
                                        id='exportIncludeLegend'
                                        checked={includeLegend}
                                        onChange={e => setIncludeLegend(e.target.checked)}
                                    />
                                    <span className={styles.switchSlider} />
                                </label>
                            </div>

                            {includeLegend && (
                                <>
                                    <span className={styles.label}>{t`Расположение условных`}</span>
                                    <Select
                                        {...SELECT_PROPS}
                                        className={styles.input}
                                        MenuProps={SELECT_MENU_PROPS}
                                        value={legendPosition}
                                        onChange={e => setLegendPosition(e.target.value)}
                                        fullWidth
                                    >
                                        {LEGEND_POSITION_KEYS.map(k => (
                                            <MenuItem
                                                key={k}
                                                value={LEGEND_POSITIONS[k]}
                                            >
                                                {i18n._(k)}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    <span className={styles.label}>{t`Размер условных`}</span>
                                    <Select
                                        {...SELECT_PROPS}
                                        className={styles.input}
                                        MenuProps={SELECT_MENU_PROPS}
                                        value={legendScale}
                                        onChange={e => setLegendScale(e.target.value)}
                                        fullWidth
                                    >
                                        {LEGEND_SCALE_KEYS.map(k => (
                                            <MenuItem
                                                key={k}
                                                value={LEGEND_SCALES[k]}
                                            >
                                                {i18n._(k)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </>
                            )}
                        </div>

                        <div className={styles.preview}>
                            {previewUrl ? (
                                <img
                                    className={styles.previewImg}
                                    src={previewUrl}
                                    alt={t`Превью экспорта`}
                                />
                            ) : (
                                <span className={styles.previewPlaceholder}>{t`Готовим превью…`}</span>
                            )}
                        </div>
                    </div>

                    <Accordion
                        className={styles.advanced}
                        elevation={0}
                    >
                        <AccordionSummary
                            className={styles.advancedSummary}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            {t`Дополнительно`}
                        </AccordionSummary>
                        <AccordionDetails className={styles.advancedDetails}>
                            <div className={styles.advancedCol}>
                                <span className={styles.label}>{t`Формат файла`}</span>
                                <ButtonGroup
                                    variant='contained'
                                    size='small'
                                    fullWidth
                                >
                                    {FORMAT_KEYS.map(f => (
                                        <Button
                                            key={f}
                                            color={format === f ? 'primary' : 'default'}
                                            onClick={() => setFormat(f)}
                                        >
                                            {f.toUpperCase()}
                                        </Button>
                                    ))}
                                </ButtonGroup>

                                <div className={styles.qualitySlot}>
                                    {format === 'jpeg' && (
                                        <>
                                            <span className={styles.label}>{`${t`Качество JPEG`} — ${Math.round(quality * 100)}%`}</span>
                                            <Slider
                                                className={styles.slider}
                                                value={quality}
                                                onChange={(e, v) => setQuality(v)}
                                                min={0.5}
                                                max={1}
                                                step={0.01}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className={styles.advancedCol}>
                                <span className={styles.label}>{t`Разрешение`}</span>
                                <Select
                                    {...SELECT_PROPS}
                                    className={styles.input}
                                    MenuProps={SELECT_MENU_PROPS}
                                    value={scale}
                                    onChange={e => setScale(e.target.value)}
                                    fullWidth
                                >
                                    {resolutions.map(r => (
                                        <MenuItem
                                            key={r.scale}
                                            value={r.scale}
                                        >
                                            {r.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div className={styles.actions}>
                    <Button
                        variant='contained'
                        size='small'
                        color='default'
                        onClick={() => setOpen(false)}
                        disabled={busy}
                    >{t`Отмена`}</Button>
                    <Button
                        variant='contained'
                        size='small'
                        color='primary'
                        onClick={handleExport}
                        disabled={busy}
                    >
                        {busy ? t`Генерация…` : t`Скачать`}
                    </Button>
                </div>
            </Dialog>
        </>
    );
};
const mapStateToProps = ({ displayedLayers, mapFilters, map }) => ({
    displayedLayers,
    mapFilters,
    selectedRegion: map.selectedRegion,
    // Контролы вне mapFilters (для exportTitle через ctx.controls).
    selectedRebalanceDate: map.selectedRebalanceDate,
    selectedRebalanceDayMode: map.selectedRebalanceDayMode,
    selectedRebalancePeriod: map.selectedRebalancePeriod,
    selectedHour: map.selectedHour,
    selectedDate: map.selectedDate,
});

export const MapExport = connect(mapStateToProps)(MapExportComponent);
